import { createContext } from "react";

export type GetFreshIdTokenOptions = Readonly<{
  forceRefresh?: boolean;
}>;

export type GetFreshIdToken = (
  options?: GetFreshIdTokenOptions,
) => Promise<string | undefined>;

export type MarkSessionExpired = () => void;

export type IdTokenProviderType = {
  idToken: string | undefined;
  getFreshIdToken: GetFreshIdToken;
  markSessionExpired: MarkSessionExpired;
};

type IdTokenSession = Readonly<{
  tokens?: Readonly<{
    idToken?: Readonly<{ toString: () => string }>;
  }>;
}>;

type FetchIdTokenSession = (options: {
  forceRefresh: true;
}) => Promise<IdTokenSession>;

const ID_TOKEN_REFRESH_BUFFER_MS = 2 * 60 * 1000;

export class IdTokenRefreshError extends Error {
  constructor() {
    super("The authenticated session could not be refreshed.");
    this.name = "IdTokenRefreshError";
  }
}

export class IdTokenUnavailableError extends Error {
  constructor() {
    super("The authentication session is temporarily unavailable.");
    this.name = "IdTokenUnavailableError";
  }
}

export const isTerminalAuthSessionError = (error: unknown): boolean => {
  if (typeof error !== "object" || error === null || !("name" in error)) {
    return false;
  }
  const name = (error as { name?: unknown }).name;
  return (
    typeof name === "string" &&
    (name === "UserUnAuthenticatedException" ||
      name === "TokenRefreshException" ||
      name.startsWith("NotAuthorizedException"))
  );
};

const tokenExpirationMs = (token: string): number | undefined => {
  try {
    const encodedPayload = token.split(".")[1];
    if (!encodedPayload) return undefined;
    const base64 = encodedPayload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=");
    const payload = JSON.parse(atob(padded)) as { exp?: unknown };
    return typeof payload.exp === "number" && Number.isFinite(payload.exp)
      ? payload.exp * 1000
      : undefined;
  } catch {
    return undefined;
  }
};

const tokenIsFreshEnough = (
  token: string | undefined,
  nowMs: number,
): token is string => {
  if (!token) return false;
  const expiration = tokenExpirationMs(token);
  return (
    expiration !== undefined && expiration - nowMs > ID_TOKEN_REFRESH_BUFFER_MS
  );
};

export const createFreshIdTokenGetter = ({
  getCurrentToken,
  fetchSession,
  publishToken,
  onSessionExpired,
  now = Date.now,
}: {
  getCurrentToken: () => string | undefined;
  fetchSession: FetchIdTokenSession;
  publishToken: (token: string) => void;
  onSessionExpired?: MarkSessionExpired;
  now?: () => number;
}): GetFreshIdToken => {
  let refreshPromise: Promise<string> | null = null;
  const expireSession = (): never => {
    try {
      onSessionExpired?.();
    } finally {
      throw new IdTokenRefreshError();
    }
  };
  const refreshForCaller = (
    pending: Promise<string>,
    forceRefresh: boolean,
  ): Promise<string> => {
    if (forceRefresh) return pending;
    return pending.catch((error: unknown) => {
      if (error instanceof IdTokenRefreshError) throw error;
      const fallbackToken = getCurrentToken();
      if (tokenIsFreshEnough(fallbackToken, now())) return fallbackToken;
      throw error;
    });
  };

  return async ({ forceRefresh = false } = {}) => {
    const currentToken = getCurrentToken();
    if (!forceRefresh && tokenIsFreshEnough(currentToken, now())) {
      return currentToken;
    }

    if (refreshPromise) {
      return refreshForCaller(refreshPromise, forceRefresh);
    }

    const pending = fetchSession({ forceRefresh: true })
      .catch((error: unknown) => {
        if (isTerminalAuthSessionError(error)) {
          return expireSession();
        }
        throw error;
      })
      .then((session) => {
        const refreshedToken = session.tokens?.idToken?.toString();
        if (!refreshedToken) {
          const fallbackToken = getCurrentToken();
          if (tokenIsFreshEnough(fallbackToken, now())) {
            throw new IdTokenUnavailableError();
          }
          return expireSession();
        }
        publishToken(refreshedToken);
        return refreshedToken;
      });
    refreshPromise = pending;
    const clearPending = () => {
      if (refreshPromise === pending) refreshPromise = null;
    };
    void pending.then(clearPending, clearPending);
    return refreshForCaller(pending, forceRefresh);
  };
};

export const IdTokenContext = createContext<IdTokenProviderType>({
  idToken: undefined,
  getFreshIdToken: async () => undefined,
  markSessionExpired: () => {},
});
